import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { 
  Users, 
  Calendar, 
  Eye, 
  Sun, 
  Moon, 
  CheckCircle, 
  XCircle, 
  Target,
  Lightbulb,
  TrendingUp,
  Filter,
  Search,
  ChevronDown,
  ChevronUp,
  CalendarDays,
  CalendarRange,
  CalendarClock,
  BarChart,
  Award,
  Star,
  RefreshCw,
  Trash2,
  Clock,
  FileText,
  AlertCircle,
  Edit3,
  Building2,
  UserCheck,
  Activity,
  CheckSquare,
  Briefcase,
  UserPlus,
  Crown,
  Users2,
  Heart,
  Smile,
  AlertTriangle,
  ThumbsUp
} from 'lucide-react';

const TeamSummaries = () => {
  const { currentUser } = useAuth();
  const { users, departments, teams, projects } = useData();
  
  const [loading, setLoading] = useState(true);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [expandedItem, setExpandedItem] = useState(null);
  const [viewMode, setViewMode] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [activeRoleTab, setActiveRoleTab] = useState('all');

  // קבלת המשתמשים הרלוונטיים לפי תפקיד
  const getRelevantUsers = () => {
    if (!currentUser) return [];
    
    let relevantUsers = [];
    
    switch (currentUser.role) {
      case 'team_manager':
        // מנהל צוות רואה את כל העובדים בצוות שלו (כולל עצמו)
        relevantUsers = users.filter(user => 
          user.teamId === currentUser.teamId && user.isActive !== false
        );
        console.log(`🔵 Team Manager: Found ${relevantUsers.length} users in team ${currentUser.teamId}`);
        break;
        
      case 'dept_manager':
        // מנהל מחלקה רואה את כל העובדים במחלקה שלו (כל הצוותים במחלקה)
        relevantUsers = users.filter(user => 
          user.departmentId === currentUser.departmentId && user.isActive !== false
        );
        console.log(`🔵 Dept Manager: Found ${relevantUsers.length} users in department ${currentUser.departmentId}`);
        break;
        
      case 'ceo':
      case 'deputy_ceo':
      case 'org_admin':
        // מנכ"ל וסמנכ"ל רואים את כל העובדים הפעילים
        relevantUsers = users.filter(user => user.isActive !== false);
        console.log(`🔵 CEO/Deputy: Found ${relevantUsers.length} active users`);
        break;
        
      default:
        // עובדים רגילים רואים רק את עצמם
        relevantUsers = users.filter(user => 
          user.id === currentUser.id || user.uid === currentUser.uid
        );
        console.log(`🔵 Employee: Can only see own summaries`);
        break;
    }
    
    return relevantUsers.filter(u => u); // מסיר null/undefined
  };

  // סינון משתמשים לפי תפקיד
  const getUsersByRole = (role) => {
    const relevantUsers = getRelevantUsers();
    
    switch(role) {
      case 'deputies':
        return relevantUsers.filter(user => user.role === 'deputy_ceo');
      case 'dept_managers':
        return relevantUsers.filter(user => user.role === 'dept_manager');
      case 'team_managers':
        return relevantUsers.filter(user => user.role === 'team_manager');
      case 'employees':
        return relevantUsers.filter(user => user.role === 'employee' || user.role === 'worker');
      default:
        return relevantUsers;
    }
  };

  const relevantUsers = getRelevantUsers();
  
  // קבלת הצוותים הרלוונטיים לפי תפקיד
  const relevantTeams = (() => {
    if (!currentUser) return [];
    
    if (currentUser.role === 'dept_manager') {
      const deptTeams = teams.filter(team => team.departmentId === currentUser.departmentId);
      console.log(`🟢 Dept Manager: Found ${deptTeams.length} teams in department`);
      return deptTeams;
    }
    
    if (currentUser.role === 'team_manager') {
      const myTeam = teams.filter(team => team.id === currentUser.teamId);
      console.log(`🟢 Team Manager: Found ${myTeam.length} team(s)`);
      return myTeam;
    }
    
    // מנכ"ל וסמנכ"ל רואים את כל הצוותים
    if (currentUser.role === 'ceo' || currentUser.role === 'deputy_ceo' || currentUser.role === 'org_admin') {
      console.log(`🟢 CEO/Deputy: Can see all ${teams.length} teams`);
      return teams;
    }
    
    // עובדים רגילים לא צריכים לראות סינון לפי צוות
    return [];
  })();

  useEffect(() => {
    loadAllData();
  }, [currentUser]);

  useEffect(() => {
    filterData();
  }, [allData, selectedUser, selectedTeam, selectedDepartment, viewMode, searchTerm, dateRange, selectedDate, activeRoleTab]);

  const loadAllData = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const dailyPlans = JSON.parse(localStorage.getItem('dailyPlans') || '[]');
      const dailySummaries = JSON.parse(localStorage.getItem('daySummaries') || '[]');
      const weeklyPlans = JSON.parse(localStorage.getItem('weeklyPlans') || '[]');
      const weeklySummaries = JSON.parse(localStorage.getItem('weeklySummaries') || '[]');
      const periodSummaries = JSON.parse(localStorage.getItem('periodSummaries') || '[]');
      
      // סינון לפי הרשאות - לוגיקה משופרת
      const filterByPermissions = (item) => {
        // מנכ"ל, סמנכ"ל ומנהל ראשי רואים הכל
        if (currentUser.role === 'ceo' || currentUser.role === 'deputy_ceo' || currentUser.role === 'org_admin') {
          return true;
        }
        
        // מצא את המשתמש שיצר את הפריט
        const itemUser = users.find(u => u.id === item.userId || u.uid === item.userId);
        if (!itemUser) {
          console.warn('Could not find user for item:', item.userId);
          return item.userId === currentUser.id; // נפילה לבדיקה בסיסית
        }
        
        // מנהל מחלקה רואה רק פריטים של עובדים במחלקה שלו
        if (currentUser.role === 'dept_manager') {
          return itemUser.departmentId === currentUser.departmentId;
        }
        
        // מנהל צוות רואה רק פריטים של עובדים בצוות שלו
        if (currentUser.role === 'team_manager') {
          return itemUser.teamId === currentUser.teamId;
        }
        
        // עובדים רואים רק את הפריטים שלהם
        return item.userId === currentUser.id || item.userId === currentUser.uid;
      };
      
      // איחוד כל הנתונים עם סוג מתאים
      console.log('📥 Loading data from localStorage:');
      console.log(`  - Daily Plans: ${dailyPlans.length} (${dailyPlans.filter(filterByPermissions).length} accessible)`);
      console.log(`  - Daily Summaries: ${dailySummaries.length} (${dailySummaries.filter(filterByPermissions).length} accessible)`);
      console.log(`  - Weekly Plans: ${weeklyPlans.length} (${weeklyPlans.filter(filterByPermissions).length} accessible)`);
      console.log(`  - Weekly Summaries: ${weeklySummaries.length} (${weeklySummaries.filter(filterByPermissions).length} accessible)`);
      console.log(`  - Period Summaries: ${periodSummaries.length} (${periodSummaries.filter(filterByPermissions).length} accessible)`);
      
      const allItems = [
        ...dailyPlans.filter(filterByPermissions).map(item => ({ ...item, type: 'daily-plan', category: 'plan' })),
        ...dailySummaries.filter(filterByPermissions).map(item => ({ ...item, type: 'daily-summary', category: 'summary' })),
        ...weeklyPlans.filter(filterByPermissions).map(item => ({ ...item, type: 'weekly-plan', category: 'plan' })),
        ...weeklySummaries.filter(filterByPermissions).map(item => ({ ...item, type: 'weekly-summary', category: 'summary' })),
        ...periodSummaries.filter(filterByPermissions).map(item => ({ 
          ...item, 
          type: item.type || `${item.summaryType}-summary`, 
          category: 'summary' 
        }))
      ];
      
      console.log(`📦 Total items loaded: ${allItems.length}`);
      
      // מיון לפי תאריך
      allItems.sort((a, b) => {
        const dateA = new Date(a.date || a.savedAt || a.createdAt || a.summaryData?.date || a.summaryData?.weekStart);
        const dateB = new Date(b.date || b.savedAt || b.createdAt || b.summaryData?.date || b.summaryData?.weekStart);
        return dateB - dateA;
      });
      
      setAllData(allItems);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    console.log('🔍 Starting filterData with', allData.length, 'items');
    let filtered = [...allData];
    
    // סינון לפי טאב תפקיד
    if (activeRoleTab !== 'all') {
      const roleUsers = getUsersByRole(activeRoleTab);
      const roleUserIds = roleUsers.map(u => u.id || u.uid);
      const beforeCount = filtered.length;
      filtered = filtered.filter(item => roleUserIds.includes(item.userId));
      console.log(`  📊 Role filter (${activeRoleTab}): ${beforeCount} → ${filtered.length} items`);
    }
    
    // סינון לפי סוג תצוגה
    if (viewMode !== 'all') {
      const beforeCount = filtered.length;
      filtered = filtered.filter(item => item.type === viewMode);
      console.log(`  📊 View mode filter (${viewMode}): ${beforeCount} → ${filtered.length} items`);
    }
    
    // סינון לפי משתמש
    if (selectedUser) {
      const beforeCount = filtered.length;
      filtered = filtered.filter(item => item.userId === selectedUser);
      const user = users.find(u => u.id === selectedUser);
      console.log(`  📊 User filter (${user?.name}): ${beforeCount} → ${filtered.length} items`);
    }
    
    // סינון לפי צוות - משופר
    if (selectedTeam) {
      const beforeCount = filtered.length;
      // סינון לפי המשתמש שיצר את הפריט ששייך לצוות
      filtered = filtered.filter(item => {
        const itemUser = users.find(u => u.id === item.userId || u.uid === item.userId);
        return itemUser && itemUser.teamId === selectedTeam;
      });
      const team = teams.find(t => t.id === selectedTeam);
      console.log(`  📊 Team filter (${team?.name}): ${beforeCount} → ${filtered.length} items`);
    }
    
    // סינון לפי מחלקה - משופר
    if (selectedDepartment) {
      const beforeCount = filtered.length;
      // סינון לפי המשתמש שיצר את הפריט ששייך למחלקה
      filtered = filtered.filter(item => {
        const itemUser = users.find(u => u.id === item.userId || u.uid === item.userId);
        return itemUser && itemUser.departmentId === selectedDepartment;
      });
      const dept = departments.find(d => d.id === selectedDepartment);
      console.log(`  📊 Department filter (${dept?.name}): ${beforeCount} → ${filtered.length} items`);
    }
    
    // סינון לפי חיפוש טקסט
    if (searchTerm) {
      const beforeCount = filtered.length;
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        return (
          item.userName?.toLowerCase().includes(searchLower) ||
          item.summary?.achievements?.toLowerCase().includes(searchLower) ||
          item.summary?.challenges?.toLowerCase().includes(searchLower) ||
          item.summary?.whatWorkedWell?.toLowerCase().includes(searchLower) ||
          item.summary?.lessonsLearned?.toLowerCase().includes(searchLower) ||
          item.summaryData?.achievements?.toLowerCase().includes(searchLower)
        );
      });
      console.log(`  📊 Search filter ("${searchTerm}"): ${beforeCount} → ${filtered.length} items`);
    }
    
    // סינון לפי טווח תאריכים
    if (dateRange.start || dateRange.end) {
      const beforeCount = filtered.length;
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date || item.savedAt || item.createdAt || item.summaryData?.date || item.summaryData?.weekStart);
        if (dateRange.start && itemDate < new Date(dateRange.start)) return false;
        if (dateRange.end && itemDate > new Date(dateRange.end)) return false;
        return true;
      });
      console.log(`  📊 Date range filter: ${beforeCount} → ${filtered.length} items`);
    }
    
    console.log(`✅ Final filtered data: ${filtered.length} items`);
    setFilteredData(filtered);
  };

  const deleteItem = async (item) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק פריט זה?')) return;
    
    try {
      const storageKey = 
        item.type === 'daily-plan' ? 'dailyPlans' :
        item.type === 'daily-summary' || item.type === 'day-summary' ? 'daySummaries' :
        item.type === 'weekly-plan' ? 'weeklyPlans' :
        item.type === 'weekly-summary' || item.type === 'week-summary' ? 'weeklySummaries' :
        'periodSummaries';
      
      const items = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const updatedItems = items.filter(i => 
        i.id !== item.id && 
        !(i.date === item.date && i.userId === item.userId && i.savedAt === item.savedAt)
      );
      
      localStorage.setItem(storageKey, JSON.stringify(updatedItems));
      await loadAllData();
      alert('הפריט נמחק בהצלחה');
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('שגיאה במחיקת הפריט');
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'daily-plan': return <Calendar className="w-5 h-5 text-blue-500" />;
      case 'daily-summary': return <Moon className="w-5 h-5 text-indigo-500" />;
      case 'weekly-plan': return <CalendarDays className="w-5 h-5 text-green-500" />;
      case 'weekly-summary': return <CalendarRange className="w-5 h-5 text-purple-500" />;
      case 'day-summary': return <Sun className="w-5 h-5 text-yellow-500" />;
      case 'week-summary': return <CalendarDays className="w-5 h-5 text-green-600" />;
      case 'month-summary': return <CalendarRange className="w-5 h-5 text-blue-600" />;
      case 'quarter-summary': return <CalendarClock className="w-5 h-5 text-purple-600" />;
      case 'year-summary': return <Award className="w-5 h-5 text-red-600" />;
      default: return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'daily-plan': return 'תכנון יומי';
      case 'daily-summary': return 'סיכום יומי';
      case 'weekly-plan': return 'תכנון שבועי';
      case 'weekly-summary': return 'סיכום שבועי';
      case 'day-summary': return 'סיכום יום';
      case 'week-summary': return 'סיכום שבוע';
      case 'month-summary': return 'סיכום חודש';
      case 'quarter-summary': return 'סיכום רבעון';
      case 'year-summary': return 'סיכום שנה';
      default: return 'לא ידוע';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'לא זמין';
    const dateObj = date?.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('he-IL', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getFeelingEmoji = (value) => {
    switch(value) {
      case 1: return '😔';
      case 2: return '😕';
      case 3: return '😐';
      case 4: return '😊';
      case 5: return '😄';
      default: return '😐';
    }
  };

  const renderTasksList = (tasks) => {
    if (!tasks || tasks.length === 0) return <p className="text-gray-500">אין משימות</p>;
    
    return (
      <ul className="space-y-2">
        {tasks.map((task, index) => (
          <li key={index} className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
            <div className="flex-1">
              <span className="font-medium">{task.title || task}</span>
              {task.description && (
                <p className="text-sm text-gray-600 mt-1">{task.description}</p>
              )}
              {task.priority && (
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                  task.priority === 'high' ? 'bg-red-100 text-red-700' :
                  task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {task.priority === 'high' ? 'גבוהה' : task.priority === 'medium' ? 'בינונית' : 'נמוכה'}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    );
  };

  const renderSchedule = (schedule) => {
    if (!schedule || Object.keys(schedule).length === 0) return <p className="text-gray-500">אין לוח זמנים</p>;
    
    return (
      <div className="space-y-3">
        {Object.entries(schedule).sort().map(([time, data]) => {
          if (!data) {
            return (
              <div key={time} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <Clock className="w-4 h-4 text-gray-400 mt-1" />
                <div className="flex-1">
                  <span className="font-mono font-bold text-sm text-gray-700">{time}</span>
                  <p className="text-sm text-gray-400">ללא משימה</p>
                </div>
              </div>
            );
          }
          
          return (
            <div key={time} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <Clock className="w-4 h-4 text-blue-500 mt-1" />
              <div className="flex-1">
                <span className="font-mono font-bold text-sm text-gray-700">{time}</span>
                
                {data && data.task && (
                  <div className="mt-1">
                    <p className="font-medium">{data.task.title || 'ללא כותרת'}</p>
                    {data.task.description && (
                      <p className="text-sm text-gray-600">{data.task.description}</p>
                    )}
                    {data.duration && (
                      <span className="text-xs text-gray-500">משך: {data.duration} שעות</span>
                    )}
                  </div>
                )}
                
                {data && data.tasks && data.tasks.length > 0 && (
                  <div className="mt-1 space-y-1">
                    {data.tasks.map((task, idx) => (
                      <div key={idx} className="text-sm">
                        <span className="font-medium">{task?.title || 'ללא כותרת'}</span>
                        {task?.projectName && (
                          <span className="text-gray-500 mr-2">• {task.projectName}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {data && typeof data === 'object' && data.title && !data.task && !data.tasks && (
                  <div className="mt-1">
                    <p className="font-medium">{data.title}</p>
                    {data.description && (
                      <p className="text-sm text-gray-600">{data.description}</p>
                    )}
                    {data.duration && (
                      <span className="text-xs text-gray-500">משך: {data.duration} שעות</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // טאבים לפי תפקידים
  const roleTabs = [
    { id: 'all', label: 'כולם', icon: Users, count: filteredData.length },
    { id: 'deputies', label: 'סמנכ"לים', icon: Crown, count: getUsersByRole('deputies').length },
    { id: 'dept_managers', label: 'מנהלי מחלקה', icon: Briefcase, count: getUsersByRole('dept_managers').length },
    { id: 'team_managers', label: 'מנהלי צוות', icon: Users2, count: getUsersByRole('team_managers').length },
    { id: 'employees', label: 'עובדים', icon: UserPlus, count: getUsersByRole('employees').length }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                <Users className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">סיכומי הצוות</h1>
                <p className="text-gray-600">ניהול ומעקב אחר תכנונים וסיכומים</p>
              </div>
            </div>
            <button
              onClick={loadAllData}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              רענן נתונים
            </button>
          </div>

          {/* טאבים לפי תפקידים */}
          <div className="mb-6">
            <div className="flex flex-wrap gap-2">
              {roleTabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveRoleTab(tab.id)}
                    className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-all ${
                      activeRoleTab === tab.id
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                    <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* סטטיסטיקות */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">תכנונים יומיים</p>
                  <p className="text-2xl font-bold text-blue-800">
                    {filteredData.filter(item => item.type === 'daily-plan').length}
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-blue-500" />
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-indigo-600 font-medium">סיכומים יומיים</p>
                  <p className="text-2xl font-bold text-indigo-800">
                    {filteredData.filter(item => item.type === 'daily-summary' || item.type === 'day-summary').length}
                  </p>
                </div>
                <Moon className="w-8 h-8 text-indigo-500" />
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">תכנונים שבועיים</p>
                  <p className="text-2xl font-bold text-green-800">
                    {filteredData.filter(item => item.type === 'weekly-plan').length}
                  </p>
                </div>
                <CalendarDays className="w-8 h-8 text-green-500" />
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">סיכומים שבועיים</p>
                  <p className="text-2xl font-bold text-purple-800">
                    {filteredData.filter(item => item.type === 'weekly-summary' || item.type === 'week-summary').length}
                  </p>
                </div>
                <CalendarRange className="w-8 h-8 text-purple-500" />
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 font-medium">סיכומים תקופתיים</p>
                  <p className="text-2xl font-bold text-orange-800">
                    {filteredData.filter(item => 
                      ['month-summary', 'quarter-summary', 'year-summary'].includes(item.type)
                    ).length}
                  </p>
                </div>
                <Award className="w-8 h-8 text-orange-500" />
              </div>
            </div>
          </div>

          {/* פילטרים */}
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">כל הסוגים</option>
                <option value="daily-plan">תכנונים יומיים</option>
                <option value="daily-summary">סיכומים יומיים</option>
                <option value="weekly-plan">תכנונים שבועיים</option>
                <option value="weekly-summary">סיכומים שבועיים</option>
                <option value="day-summary">סיכום יום</option>
                <option value="week-summary">סיכום שבוע</option>
                <option value="month-summary">סיכום חודש</option>
                <option value="quarter-summary">סיכום רבעון</option>
                <option value="year-summary">סיכום שנה</option>
              </select>
              
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">כל המשתמשים</option>
                {(activeRoleTab === 'all' ? relevantUsers : getUsersByRole(activeRoleTab)).map(user => (
                  <option key={user.id || user.uid} value={user.id || user.uid}>
                    {user.name}
                  </option>
                ))}
              </select>
              
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">כל הצוותים</option>
                {relevantTeams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
              
              {(currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo' || currentUser?.role === 'org_admin') && (
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">כל המחלקות</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="חיפוש..."
                  className="w-full pr-10 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
              />
              <span className="self-center">עד</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
              />
              
              <button
                onClick={() => {
                  setViewMode('all');
                  setSelectedUser('');
                  setSelectedTeam('');
                  setSelectedDepartment('');
                  setSearchTerm('');
                  setDateRange({ start: '', end: '' });
                  setActiveRoleTab('all');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all duration-200"
              >
                נקה פילטרים
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* תוכן */}
      <div className="grid gap-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">אין נתונים להצגה</p>
            <p className="text-gray-500 text-sm mt-2">נסה לשנות את הפילטרים או לרענן את הנתונים</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {filteredData.map((item, index) => {
              const user = users.find(u => u.id === item.userId || u.uid === item.userId);
              const team = teams.find(t => t.id === item.teamId);
              const department = departments.find(d => d.id === item.departmentId);
              const isExpanded = expandedItem === index;
              
              return (
                <div
                  key={`${item.id}-${index}`}
                  className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl"
                >
                  {/* כותרת */}
                  <div
                    className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedItem(isExpanded ? null : index)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {getTypeIcon(item.type)}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {getTypeLabel(item.type)} - {user?.name || item.userName || 'משתמש לא ידוע'}
                          </h3>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(item.date || item.savedAt || item.createdAt || item.summaryData?.date || item.summaryData?.weekStart)}
                            </span>
                            {team && (
                              <span className="flex items-center gap-1">
                                <Users2 className="w-4 h-4" />
                                {team.name}
                              </span>
                            )}
                            {department && (
                              <span className="flex items-center gap-1">
                                <Building2 className="w-4 h-4" />
                                {department.name}
                              </span>
                            )}
                            {(item.summary?.overallFeeling || item.summaryData?.overallFeeling || item.generalFeeling) && (
                              <span className="flex items-center gap-1">
                                <Heart className="w-4 h-4" />
                                הרגשה: {getFeelingEmoji(item.summary?.overallFeeling || item.summaryData?.overallFeeling || item.generalFeeling)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {(currentUser?.role === 'org_admin' || 
                          currentUser?.role === 'ceo' || 
                          currentUser?.id === item.userId) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteItem(item);
                            }}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                      </div>
                    </div>
                  </div>

      {/* תוכן מורחב */}
{isExpanded && (
  <div className="border-t border-gray-200 p-6 space-y-6 bg-gray-50">
    
    {/* תכנון יומי */}
    {item.type === 'daily-plan' && (
      <>
        {item.selectedTasks && item.selectedTasks.length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-blue-500" />
              משימות מתוכננות ({item.selectedTasks.length})
            </h4>
            {renderTasksList(item.selectedTasks)}
          </div>
        )}
        
        {item.schedule && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-500" />
              לוח זמנים
            </h4>
            {renderSchedule(item.schedule)}
          </div>
        )}
        
        {item.dayNotes && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-gray-500" />
              הערות
            </h4>
            <p className="text-gray-700 bg-white p-3 rounded-lg">
              {item.dayNotes}
            </p>
          </div>
        )}
      </>
    )}

    {/* תכנון שבועי מלא עם טבלה */}
    {item.type === 'weekly-plan' && item.summaryData && (
      <>
        <div className="col-span-2">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-green-500" />
            לוח זמנים שבועי
          </h4>
          
          {/* סטטיסטיקות שבועיות */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded-xl">
              <div className="text-2xl font-bold text-blue-700">
                {item.summaryData.totalTasks || 0}
              </div>
              <div className="text-sm text-blue-600">סה"כ משימות</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-xl">
              <div className="text-2xl font-bold text-green-700">
                {item.summaryData.scheduledTasks || 0}
              </div>
              <div className="text-sm text-green-600">משימות מתוכננות</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-xl">
              <div className="text-2xl font-bold text-purple-700">
                {Object.keys(item.summaryData.schedule || {}).length}
              </div>
              <div className="text-sm text-purple-600">ימים פעילים</div>
            </div>
          </div>

          {/* טבלת לוח שבועי */}
          {item.summaryData.schedule && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="p-3 bg-gray-100 border border-gray-300 text-right sticky left-0 z-10">
                      שעה
                    </th>
                    {Object.keys(item.summaryData.schedule).sort().map(dateStr => {
                      const date = new Date(dateStr);
                      const dayNames = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
                      const dayName = dayNames[date.getDay()];
                      
                      return (
                        <th key={dateStr} className="p-3 bg-blue-50 border border-gray-300 text-center min-w-[150px]">
                          <div className="font-bold">{dayName}</div>
                          <div className="text-xs text-gray-600">
                            {date.toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric' })}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {/* יצירת שורות לכל שעה */}
                  {['06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', 
                    '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', 
                    '20:00', '21:00', '22:00'].map(time => (
                    <tr key={time} className="hover:bg-gray-50">
                      <td className="p-2 bg-gray-50 border border-gray-300 font-mono text-sm font-bold sticky left-0">
                        {time}
                      </td>
                      {Object.keys(item.summaryData.schedule).sort().map(dateStr => {
                        const daySchedule = item.summaryData.schedule[dateStr];
                        const taskAtTime = daySchedule?.[time];
                        
                        return (
                          <td key={`${dateStr}-${time}`} className="p-2 border border-gray-300">
                            {taskAtTime ? (
                              <div className={`p-2 rounded text-xs ${
                                taskAtTime.priority === 'high' ? 'bg-red-100 text-red-800' :
                                taskAtTime.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                <div className="font-bold truncate">{taskAtTime.title || 'ללא כותרת'}</div>
                                {taskAtTime.duration && taskAtTime.duration > 0.5 && (
                                  <div className="text-xs opacity-75 mt-1">
                                    {taskAtTime.duration}h
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-xs text-gray-300 text-center">-</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {item.weekNotes && (
            <div className="mt-6">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-gray-500" />
                הערות שבועיות
              </h4>
              <p className="text-gray-700 bg-white p-3 rounded-lg">
                {item.weekNotes}
              </p>
            </div>
          )}
        </div>
      </>
    )}

    {/* סיכומים מהטופס החדש (יום, שבוע, חודש, רבעון, שנה) */}
    {['day-summary', 'week-summary', 'month-summary', 'quarter-summary', 'year-summary'].includes(item.type) && (
      <>
        {/* תאריכים */}
        {item.dateRange && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              תקופה
            </h4>
            <div className="bg-white p-4 rounded-lg">
              <p className="text-gray-700">
                מתאריך: <span className="font-semibold">{new Date(item.dateRange.start).toLocaleDateString('he-IL')}</span>
              </p>
              <p className="text-gray-700">
                עד תאריך: <span className="font-semibold">{new Date(item.dateRange.end).toLocaleDateString('he-IL')}</span>
              </p>
            </div>
          </div>
        )}

        {/* הרגשה כללית */}
        {item.generalFeeling && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              הרגשה כללית
            </h4>
            <div className="bg-white p-4 rounded-lg text-center">
              <div className="text-4xl mb-2">{getFeelingEmoji(item.generalFeeling)}</div>
              <div className="text-gray-700">ציון: {item.generalFeeling}/5</div>
            </div>
          </div>
        )}

        {/* משימות שהושלמו/לא הושלמו - לסיכום יומי */}
        {item.completedTasks && item.completedTasks.length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              משימות שהושלמו ({item.completedTasks.length})
            </h4>
            {renderTasksList(item.completedTasks)}
          </div>
        )}
        
        {item.incompleteTasks && item.incompleteTasks.length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              משימות שלא הושלמו ({item.incompleteTasks.length})
            </h4>
            {renderTasksList(item.incompleteTasks)}
            
            {item.taskExplanations && Object.keys(item.taskExplanations).length > 0 && (
              <div className="mt-4 space-y-2">
                <h5 className="text-sm font-medium text-gray-700">הסברים למשימות שלא הושלמו:</h5>
                {Object.entries(item.taskExplanations).map(([taskId, explanation]) => {
                  const task = item.incompleteTasks.find(t => t.id === taskId);
                  return (
                    <div key={taskId} className="p-3 bg-red-50 rounded-lg">
                      {task && <p className="font-medium text-red-900 mb-1">{task.title}:</p>}
                      <p className="text-sm text-red-800">{explanation}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* כל שאלות הסיכום */}
        {item.summary && (
          <div className="grid gap-4">
            {/* עמידה ביעדים */}
            {item.summary.goalsAchieved && (
              <div className="bg-white p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Target className="w-5 h-5 text-green-500" />
                  האם עמדת ביעדים שהצבת לעצמך?
                </h4>
                <p className="text-gray-700">{item.summary.goalsAchieved}</p>
              </div>
            )}

            {/* ביצוע משימות */}
            {item.summary.tasksCompleted && (
              <div className="bg-white p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-blue-500" />
                  האם כל המשימות בוצעו כמו שצריך?
                </h4>
                <p className="text-gray-700">{item.summary.tasksCompleted}</p>
              </div>
            )}

            {/* מכשולים */}
            {item.summary.unexpectedObstacles && (
              <div className="bg-white p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  האם היו מכשולים לא צפויים בדרך?
                </h4>
                <p className="text-gray-700">{item.summary.unexpectedObstacles}</p>
              </div>
            )}

            {/* התמודדות עם מכשולים */}
            {item.summary.obstacleHandling && (
              <div className="bg-white p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-500" />
                  איך התמודדת עם המכשולים?
                </h4>
                <p className="text-gray-700">{item.summary.obstacleHandling}</p>
              </div>
            )}

            {/* שיפור ושימור - בשלושה עמודות */}
            <div className="grid md:grid-cols-3 gap-4">
              {item.summary.whatWorkedWell && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                    <Star className="w-5 h-5 text-green-600" />
                    מה עשית טוב ותרצה לשמר
                  </h4>
                  <p className="text-green-800">{item.summary.whatWorkedWell}</p>
                </div>
              )}
              
              {item.summary.lessonsLearned && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-blue-600" />
                    מה למדת מהתקופה
                  </h4>
                  <p className="text-blue-800">{item.summary.lessonsLearned}</p>
                </div>
              )}
              
              {item.summary.areasToImprove && (
                <div className="bg-orange-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-orange-600" />
                    מה צריך לשפר
                  </h4>
                  <p className="text-orange-800">{item.summary.areasToImprove}</p>
                </div>
              )}
            </div>

            {/* תובנות נוספות */}
            <div className="grid md:grid-cols-2 gap-4">
              {item.summary.keyHighlights && (
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                    <Award className="w-5 h-5 text-purple-600" />
                    נקודות שיא של התקופה
                  </h4>
                  <p className="text-purple-800">{item.summary.keyHighlights}</p>
                </div>
              )}

              {item.summary.teamCollaboration && (
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-indigo-900 mb-2 flex items-center gap-2">
                    <Users2 className="w-5 h-5 text-indigo-600" />
                    שיתוף פעולה עם הצוות
                  </h4>
                  <p className="text-indigo-800">{item.summary.teamCollaboration}</p>
                </div>
              )}
            </div>

            {/* התפתחות אישית ויעדים */}
            <div className="grid md:grid-cols-2 gap-4">
              {item.summary.personalGrowth && (
                <div className="bg-teal-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-teal-900 mb-2 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-teal-600" />
                    התפתחות אישית ומקצועית
                  </h4>
                  <p className="text-teal-800">{item.summary.personalGrowth}</p>
                </div>
              )}

              {item.summary.nextPeriodGoals && (
                <div className="bg-pink-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-pink-900 mb-2 flex items-center gap-2">
                    <Target className="w-5 h-5 text-pink-600" />
                    יעדים לתקופה הבאה
                  </h4>
                  <p className="text-pink-800">{item.summary.nextPeriodGoals}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </>
    )}

    {/* סיכום יומי/שבועי ישן */}
    {(item.type === 'daily-summary' || item.type === 'weekly-summary') && item.summary && (
      <>
        {item.summary.achievements && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-500" />
              הישגים
            </h4>
            <p className="text-gray-700 bg-white p-3 rounded-lg">
              {item.summary.achievements}
            </p>
          </div>
        )}

        {item.summary.challenges && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              אתגרים
            </h4>
            <p className="text-gray-700 bg-white p-3 rounded-lg">
              {item.summary.challenges}
            </p>
          </div>
        )}

        {item.completedTasks && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              משימות שהושלמו ({item.completedTasks.length})
            </h4>
            {renderTasksList(item.completedTasks)}
          </div>
        )}

        {item.incompleteTasks && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              משימות שלא הושלמו ({item.incompleteTasks.length})
            </h4>
            {renderTasksList(item.incompleteTasks)}
          </div>
        )}
      </>
    )}
    
  </div>
)}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamSummaries;